import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-fg border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm ' +
    'hover:bg-accent-hover hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker ' +
    'active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed',
  secondary:
    'bg-surface text-fg border-2 border-fg/85 dark:border-fg/40 shadow-sticker-sm ' +
    'hover:bg-surface-hover hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker ' +
    'active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed',
  outline:
    'bg-transparent text-fg hover:bg-surface-hover border border-border-strong',
  ghost:
    'bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg',
  subtle:
    'bg-bg-inset text-fg hover:bg-surface-hover border border-border',
  danger:
    'bg-danger/10 text-danger border-2 border-danger/60 shadow-sticker-sm ' +
    'hover:bg-danger/15 hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-sticker ' +
    'active:translate-x-[1px] active:translate-y-[1px] active:shadow-sticker-pressed',
};

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-sm gap-2 rounded-lg font-medium',
  icon: 'h-9 w-9 rounded-lg',
  'icon-sm': 'h-7 w-7 rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'secondary', size = 'md', loading, icon, iconRight, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-semibold select-none transition-all duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <span className="inline-flex">
          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
            <path d="M22 12a10 10 0 0 1-10 10" />
          </svg>
        </span>
      ) : icon ? (
        <span className="inline-flex shrink-0">{icon}</span>
      ) : null}
      {children}
      {iconRight && !loading ? <span className="inline-flex shrink-0">{iconRight}</span> : null}
    </button>
  ),
);
Button.displayName = 'Button';
